import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./common.css";

const KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, "←", 0, "✔"];

export default function Pin() {
  const [pin, setPin] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | error | success
  const [shake, setShake] = useState(false);
  const [pressedKey, setPressedKey] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const cardRef = useRef(null);
  const nav = useNavigate();

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const verify = useCallback(async (pinOverride) => {
    const currentPin = pinOverride ?? pin;
    if (currentPin.length !== 4) return;

    setStatus("loading");
    const token = localStorage.getItem("token");

    try {
      const res = await axios.post(
        "https://atmsim-pip.onrender.com/api/pin",
        { pin: currentPin },
        { headers: { Authorization: token } }
      );

      if (res.data.status === "ok") {
        setStatus("success");
        cardRef.current?.classList.add("exit");
        setTimeout(() => nav("/dashboard"), 900);
      } else {
        throw new Error("wrong");
      }
    } catch {
      setAttempts((a) => a + 1);
      setStatus("error");
      triggerShake();
      setPin("");
      setTimeout(() => setStatus("idle"), 1500);
    }
  }, [pin, nav]);


  const handlePress = useCallback((k) => {
    if (status === "loading" || status === "success") return;

    setPressedKey(k);
    setTimeout(() => setPressedKey(null), 150);

    if (k === "←") {
      setPin((p) => p.slice(0, -1));
      setStatus("idle");
    } else if (k === "✔") {
      verify();
    } else {
      if (pin.length < 4) {
        const next = pin + k;
        setPin(next);
        setStatus("idle");
        if (next.length === 4) {
          setTimeout(() => verify(next), 300);
        }
      }
    }
  }, [pin, status, verify]);

  const getStatusMsg = () => {
    if (status === "loading") return { text: "VERIFYING...", cls: "msg-loading" };
    if (status === "success") return { text: "✔ ACCESS GRANTED", cls: "msg-success" };
    if (status === "error") return { text: `✘ INVALID PIN${attempts > 1 ? ` (${attempts} ATTEMPTS)` : ""}`, cls: "msg-error" };
    if (pin.length === 0) return { text: "ENTER YOUR 4-DIGIT PIN", cls: "msg-idle" };
    if (pin.length < 4) return { text: `${pin.length} OF 4 DIGITS ENTERED`, cls: "msg-idle" };
    return { text: "PRESS ✔ TO CONFIRM", cls: "msg-idle" };
  };

  const msg = getStatusMsg();

  return (
    <div className="atm-pin-page">
      <div className="scanlines" />
      <div className="grid-bg" />
      <div className="corner tl" /><div className="corner tr" />
      <div className="corner bl" /><div className="corner br" />

      <div ref={cardRef} className={`atm-card pin-card ${shake ? "shake" : ""}`}>

        <div className="card-header">
          <div className="header-icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect x="8" y="14" width="16" height="12" rx="2" stroke="#00ff88" strokeWidth="1.5"/>
              <path d="M11 14v-3a5 5 0 0 1 10 0v3" stroke="#00ff88" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="16" cy="20" r="2" fill="#00ff88" opacity="0.8"/>
              <line x1="16" y1="22" x2="16" y2="24" stroke="#00ff88" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <div className="bank-name">NAPPS BANK</div>
            <div className="bank-sub">PIN VERIFICATION REQUIRED</div>
          </div>
          <div className="header-dot" />
        </div>

        <div className="card-divider" />

        <div className={`status-msg ${msg.cls}`}>{msg.text}</div>

        <div className="pin-display">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`pin-dot-lg ${i < pin.length ? "filled" : ""} ${status === "success" && i < pin.length ? "success" : ""} ${status === "error" && i < 4 ? "error-dot" : ""}`}
            >
              {i < pin.length && status !== "loading" && <div className="dot-fill" />}
              {status === "loading" && i < pin.length && <div className="dot-pulse" />}
            </div>
          ))}
        </div>

        <div className={`keypad-grid ${status === "loading" || status === "success" ? "keypad-disabled" : ""}`}>
          {KEYS.map((k, i) => {
            const isConfirm = k === "✔";
            const isBack = k === "←";
            const isActive = pressedKey === k;
            const isReady = isConfirm && pin.length === 4 && status === "idle";

            return (
              <button
                key={i}
                className={[
                  "pin-key",
                  isConfirm ? "key-confirm" : "",
                  isBack ? "key-back" : "",
                  isActive ? "key-pressed" : "",
                  isReady ? "key-ready" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => handlePress(k)}
                disabled={status === "loading" || status === "success"}
              >
                <span className="key-label">{k}</span>
                {isConfirm && <span className="key-hint">ENTER</span>}
                {isBack && <span className="key-hint">CLR</span>}
              </button>
            );
          })}
        </div>

        {attempts >= 2 && status !== "success" && (
          <div className="attempts-warn">
            ⚠ {3 - Math.min(attempts, 3)} ATTEMPT{attempts < 2 ? "S" : ""} REMAINING
          </div>
        )}

        <div className="card-footer">
          <span>256-BIT ENCRYPTED</span>
          <span className="footer-sep">|</span>
          <span>PCI DSS COMPLIANT</span>
          <span className="footer-sep">|</span>
          <span>ISO 27001</span>
        </div>
      </div>
    </div>
  );
}